/**
 * Module dependencies.
 */

import React, { ComponentType, Fragment, useEffect, useMemo } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { FilterSelectField } from './filter-components/filter-select-field';
import { FormListFilter, ListFilter } from './form-filters-type';
import { FilterCheckboxField } from './filter-components/filter-checkbox-field';
import { FilterInputSearchField } from './filter-components/filter-input-seach-field';
import { cn } from 'app/lib/utils';

/**
 * `Props` type.
 */

type Props = {
  item: ListFilter;
  form: UseFormReturn<FieldValues, any>;
  values: any;
};

/**
 * Components.
 */

const components: Record<FormListFilter, ComponentType<any>> = {
  inputSearch: FilterInputSearchField,
  selectSearch: FilterSelectField,
  checkboxSearch: FilterCheckboxField
};

/**
 * Export `WrapperField` component.
 */

export function WrapperField({ item, form, values }: Props): JSX.Element {
  const Component = components[item.type] ?? Fragment;
  const { watch } = form;
  const { onChange } = item;
  const value = watch(item.name);
  const hasDisabled = useMemo(() => {
    return typeof item.disabled === 'function' ? item.disabled(values ?? {}) : item.disabled;
  }, [item, values]);

  useEffect(() => {
    if (!onChange) {
      return;
    }

    onChange(value, form);
  }, [form, onChange, value]);

  // Inline filters get a fixed width so search + selects sit side by side on one
  // row (a full-width field would grow and push the next filter to a new line);
  // they go full width only on mobile.
  const widthClass = item.inline
    ? item.type === 'inputSearch'
      ? 'w-[300px] max-[600px]:w-full'
      : 'w-[220px] max-[600px]:w-full'
    : 'max-w-none';

  return (
    <Component
      className={cn('p-0', widthClass)}
      disabled={hasDisabled}
      fieldClassName={undefined}
      form={form}
      item={item}
    />
  );
}
